import React, { useState, useEffect, useRef } from "react";
import TabellaPianificazione from "./TabellaPianificazione";
import DettaglioConsuntivo from "./DettaglioConsuntivo";
import Riepilogo from "./Riepilogo";
import Dashboard from "./Dashboard";
import Impostazioni from "./Impostazioni";
import Login from "./Login";
import { useAuth } from "./AuthContext";

const FONT = "Roboto, 'Segoe UI', Arial, sans-serif";
const DARK = "#1A1A1A";
const GREY = "#546E7A";
const BORDER = "#E0E0E0";

const TABS = [
  { id: "settimanale",  label: "Pianificazione Settimanale" },
  { id: "dettaglio",    label: "Dettaglio / Consuntivo" },
  { id: "riepilogo",    label: "Riepilogo" },
  { id: "dashboard",    label: "Dashboard" },
  { id: "impostazioni", label: "Impostazioni", soloAdmin: true },
];

function useOrologio() {
  const [ora, setOra] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setOra(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return ora;
}

function useHashTab(defaultTab, tabConsentite) {
  const idsConsentiti = tabConsentite.map(t => t.id);
  const hashToTab = (hash) => {
    const t = hash.replace("#", "");
    return idsConsentiti.includes(t) ? t : defaultTab;
  };
  const [tabAttiva, setTabAttivaState] = useState(() => hashToTab(window.location.hash));

  useEffect(() => {
    const onHash = () => setTabAttivaState(hashToTab(window.location.hash));
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsConsentiti.join(",")]);

  // Se il tab attivo non è (più) consentito, ricade sul default
  useEffect(() => {
    if (!idsConsentiti.includes(tabAttiva)) setTabAttivaState(defaultTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsConsentiti.join(","), tabAttiva]);

  const setTabAttiva = (id) => {
    window.location.hash = id;
  };

  return [tabAttiva, setTabAttiva];
}

export default function App() {
  const { utente, ruolo, caricamento, logout } = useAuth();
  const isAdmin = ruolo === "admin";
  const tabsVisibili = TABS.filter(t => !t.soloAdmin || isAdmin);
  const [tabAttiva, setTabAttiva] = useHashTab("settimanale", tabsVisibili);
  const ora = useOrologio();
  const headerRef = useRef(null);

  // Espone l'altezza dell'header sticky come CSS var globale, così le tabelle
  // possono ancorare i propri header subito sotto (offset dinamico al wrapping).
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const setH = () => document.documentElement.style.setProperty("--app-header-h", `${el.offsetHeight}px`);
    setH();
    const ro = new ResizeObserver(setH);
    ro.observe(el);
    window.addEventListener("resize", setH);
    return () => { ro.disconnect(); window.removeEventListener("resize", setH); };
  }, []);

  // Gate di autenticazione
  if (caricamento) {
    return (
      <div style={{ fontFamily: FONT, minHeight: "100vh", background: DARK,
        display: "flex", alignItems: "center", justifyContent: "center", color: "#90CAF9" }}>
        Caricamento…
      </div>
    );
  }
  if (!utente) return <Login />;

  return (
    <div style={{ fontFamily: FONT, minHeight: "100vh", background: "#F5F5F5" }}>

      {/* ── Header dark sticky con tab integrate ─────────────────────────── */}
      <header ref={headerRef} style={{
        background: DARK,
        position: "sticky", top: 0, zIndex: 200,
        boxShadow: "0 2px 8px rgba(0,0,0,0.35)",
      }}>
        {/* Riga superiore: loghi + titolo */}
        <div style={{
          display: "flex", alignItems: "center",
          justifyContent: "space-between",
          padding: "18px 28px 0",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            <div style={{ background: "#fff", borderRadius: 8, padding: "6px 14px", display: "flex", alignItems: "center" }}>
              <img src="/logo_impj.png" alt="Logo" style={{ height: 48, objectFit: "contain" }} />
            </div>
            <div style={{ width: 1, height: 52, background: "#444" }} />
            <div>
              <div style={{ color: "#fff", fontWeight: 700, fontSize: 26, letterSpacing: 0.5 }}>
                Pianificazione Container
              </div>
              <div style={{ color: "#90CAF9", fontSize: 14, marginTop: 3 }}>
                {tabsVisibili.find(t => t.id === tabAttiva)?.label}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: 22, letterSpacing: 1, lineHeight: 1 }}>
              {ora.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </div>
            <div style={{ color: "#90CAF9", fontSize: 13, letterSpacing: 0.3 }}>
              {ora.toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4 }}>
              <span style={{ color: "#B0BEC5", fontSize: 13 }}>
                {utente.username} · <span style={{ color: "#90CAF9" }}>{ruolo}</span>
              </span>
              <button
                onClick={logout}
                title="Esci"
                style={{
                  background: "transparent", border: "1px solid #546E7A", color: "#CFD8DC",
                  borderRadius: 6, padding: "3px 10px", fontSize: 12, cursor: "pointer",
                  fontFamily: FONT,
                }}
              >
                Esci
              </button>
              <img src="/logo_adi.png" alt="ADI" style={{ height: 32, objectFit: "contain", opacity: 0.85 }} />
            </div>
          </div>
        </div>

        {/* Tab di navigazione */}
        <nav style={{ display: "flex", padding: "0 20px", gap: 4, marginTop: 14 }}>
          {tabsVisibili.map(tab => {
            const attiva = tabAttiva === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setTabAttiva(tab.id)}
                style={{
                  background: attiva ? "#fff" : "transparent",
                  border: "none",
                  borderRadius: "8px 8px 0 0",
                  padding: "11px 26px",
                  cursor: "pointer",
                  fontWeight: attiva ? 700 : 400,
                  color: attiva ? DARK : "#90CAF9",
                  fontSize: 16,
                  fontFamily: FONT,
                  transition: "all 0.15s",
                  letterSpacing: 0.2,
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </nav>
      </header>

      {/* Contenuto */}
      <main>
        {tabAttiva === "settimanale"  && <TabellaPianificazione />}
        {tabAttiva === "dettaglio"    && <DettaglioConsuntivo />}
        {tabAttiva === "riepilogo"    && <Riepilogo />}
        {tabAttiva === "dashboard"    && <Dashboard />}
        {tabAttiva === "impostazioni" && <Impostazioni />}
      </main>

    </div>
  );
}
